import { StateMachine } from './StateMachine';
import { 
    IdleState, 
    WalkState, 
    PatrolState, 
    AttackState, 
    HurtState, 
    DeathState,
    SkillState,
    JumpState 
} from './MobStates';

export class Mob {
    constructor(scene, x, y, config = {}) {
        this.scene = scene;
        this.x = x;
        this.y = y;
        
        // Mob configuration
        this.mobType = config.mobType || 'zombie';
        this.enemyType = config.enemyType || 'melee'; // 'stationary', 'melee', 'ranged', 'boss'
        
        // Stats
        this.maxHealth = config.health || 100;
        this.currentHealth = this.maxHealth;
        this.speed = config.speed || 50;
        this.damage = config.damage || 10;
        this.attackRange = config.attackRange || 40;
        this.detectionRange = config.detectionRange || 150;
        this.attackCooldownTime = config.attackCooldown || 2000;
        this.skillCooldownTime = config.skillCooldown || 5000;
        
        // Behavior flags
        this.shouldPatrol = config.shouldPatrol || false;
        this.isAggressive = config.isAggressive !== false; // Default true
        
        // Collision configuration - rectangle at mob's origin for preventing overlap
        this.mobCollision = config.mobCollision || { x: 0, y: 0, width: 12, height: 12 };
        
        // Audio configuration - arrays of sound keys for different actions
        this.audioConfig = {
            attacks: config.attackSounds || [],
            idle: config.idleSounds || [],
            damage: config.damageSounds || [],
            death: config.deathSounds || [],
            skill: config.skillSounds || [],
            rangedHit: config.rangedHitSounds || []
        };
        
        // Idle sound timer
        this.idleSoundTimer = 0;
        this.idleSoundInterval = 5000; // 5 seconds between idle sounds
        
        // State tracking
        this.isDead = false;
        this.isInvulnerable = false;
        this.invulnerabilityDuration = 100; // Brief invulnerability after taking damage
        
        // Direction and movement
        this.facingDirection = 1; // 1 for right, -1 for left
        this.lastDamageDirection = null;
        
        // Initialize sprite and physics
        this.createSprite();
        this.setupPhysics();
        this.setupAnimations();
        
        // Initialize state machine
        this.stateMachine = new StateMachine(this);
        this.setupStateMachine();
        
        // Start in idle state
        this.stateMachine.changeState('IdleState');
    }

    createSprite() {
        // Create the sprite based on mob type
        this.sprite = this.scene.add.sprite(this.x, this.y, this.mobType);
        this.sprite.setOrigin(0.5, 1); // Bottom center origin
        this.sprite.setDepth(10); // Above background, below UI
        
        // Store reference for easy access
        this.sprite.mobInstance = this;
    }

    setupPhysics() {
        // Enable physics
        this.scene.physics.world.enable(this.sprite);
        this.body = this.sprite.body;
        
        // Set collision bounds using mobCollision configuration
        this.body.setSize(this.mobCollision.width, this.mobCollision.height);
        this.body.setOffset(
            this.mobCollision.x + (this.sprite.width - this.mobCollision.width) / 2, 
            this.mobCollision.y + (this.sprite.height - this.mobCollision.height) / 2
        );
        
        // Physics properties (matching PlayerCharacter setup)
        this.body.setCollideWorldBounds(true);
        this.body.setGravity(0); // No gravity for top-down movement
        this.body.setBounce(0);
        this.body.setMaxVelocity(this.speed * 2, this.speed * 2); // Same max for both X and Y
        this.body.setDragX(1000); // High drag for responsive stopping
        this.body.setDragY(1000);
        
        // Make mobs immovable so player can't push them around
        this.body.setImmovable(true);
    }

    setupAnimations() {
        // Animations are already loaded, we just need to reference them
        this.animations = {
            birth: `${this.mobType}_birth`,
            idle: `${this.mobType}_idle`,
            walk: `${this.mobType}_walk`,
            jump: `${this.mobType}_jump`,
            fall: `${this.mobType}_fall`,
            attack: `${this.mobType}_attack`,
            hurt: `${this.mobType}_hurt`,
            death: `${this.mobType}_death`,
            skill: `${this.mobType}_skill` // For bosses
        };
    }

    setupStateMachine() {
        // Add all possible states
        this.stateMachine.addState('IdleState', new IdleState());
        this.stateMachine.addState('WalkState', new WalkState());
        this.stateMachine.addState('PatrolState', new PatrolState());
        this.stateMachine.addState('AttackState', new AttackState());
        this.stateMachine.addState('HurtState', new HurtState());
        this.stateMachine.addState('DeathState', new DeathState());
        
        // Add special states for specific mob types
        if (this.enemyType === 'boss') {
            this.stateMachine.addState('SkillState', new SkillState());
        }
        
        if (this.mobType.includes('jump')) {
            this.stateMachine.addState('JumpState', new JumpState());
        }
    }

    update(deltaTime) {
        if (this.isDead) return;
        
        // Update state machine
        this.stateMachine.update(deltaTime);
        
        // Update sprite position to match physics body
        this.x = this.sprite.x;
        this.y = this.sprite.y;
        
        // Handle sprite flipping based on movement direction
        this.updateSpriteDirection();
        
        // Update invulnerability
        if (this.isInvulnerable) {
            this.invulnerabilityTimer -= deltaTime;
            if (this.invulnerabilityTimer <= 0) {
                this.isInvulnerable = false;
                this.sprite.setAlpha(1); // Reset transparency
            }
        }
        
        // Update idle sound timer
        this.updateIdleSounds(deltaTime);
    }

    updateSpriteDirection() {
        // Flip sprite based on facing direction
        if (this.facingDirection === -1) {
            this.sprite.setFlipX(true);
        } else {
            this.sprite.setFlipX(false);
        }
    }

    // Animation methods
    playAnimation(animationName, ignoreIfPlaying = true) {
        const animKey = this.animations[animationName];
               
        if (animKey) {
            // Check if the animation actually exists in Phaser's animation manager
            if (this.scene.anims.exists(animKey)) {
                this.sprite.play(animKey, ignoreIfPlaying);

            } else {
                console.warn(`Animation '${animKey}' does not exist for mob ${this.mobType}`);
                // Try to play a default idle animation if available
                if (animationName !== 'idle' && this.scene.anims.exists(`${this.mobType}_idle`)) {
                    this.sprite.play(`${this.mobType}_idle`, ignoreIfPlaying);
                }
            }
        } else {
            console.warn(`Animation mapping '${animationName}' not found for mob ${this.mobType}`);
        }
    }

    // Movement methods
    setVelocity(x, y) {
        this.body.setVelocity(x, y);
    }

    moveTowardsPlayer() {
        const player = this.getPlayer();
        if (!player) return;

        const direction = this.getDirectionToPlayer();
        this.facingDirection = direction.x > 0 ? 1 : -1;
        
        // Top-down movement - move in both X and Y directions
        this.setVelocity(direction.x * this.speed, direction.y * this.speed);
    }

    // Audio methods
    playRandomSound(soundType, volume = 0.4) {
        const soundArray = this.audioConfig[soundType];
        if (!soundArray || soundArray.length === 0) return;

        const randomIndex = Math.floor(Math.random() * soundArray.length);
        const soundKey = soundArray[randomIndex];

        if (this.scene.sound && this.scene.cache.audio.exists(soundKey)) {
            this.scene.sound.play(soundKey, { volume });
        }
    }

    playAttackSound() {
        this.playRandomSound('attacks', 0.4);
    }

    playDamageSound() {
        this.playRandomSound('damage', 0.5);
    }

    playDeathSound() {
        this.playRandomSound('death', 0.6);
    }

    playSkillSound() {
        this.playRandomSound('skill', 0.5);
    }

    playRangedHitSound() {
        this.playRandomSound('rangedHit', 0.4);
    }

    updateIdleSounds(deltaTime) {
        // Only play idle sounds when in idle state and mob is alive
        if (this.isDead || !this.stateMachine || this.stateMachine.getCurrentStateName() !== 'IdleState') {
            this.idleSoundTimer = 0;
            return;
        }

        this.idleSoundTimer += deltaTime;
        
        if (this.idleSoundTimer >= this.idleSoundInterval) {
            this.playRandomSound('idle', 0.3);
            this.idleSoundTimer = 0;
        }
    }

    // Player detection and interaction
    getPlayer() {
        // Assuming the player is accessible through the scene
        return this.scene.player || this.scene.gameState?.player;
    }

    getDirectionToPlayer() {
        const player = this.getPlayer();
        if (!player) return { x: 0, y: 0 };

        // Account for different sprite origins:
        // Mob uses origin(0.5, 1) - bottom center, so mob.y is at bottom
        // Player uses origin(0.5, 0.5) - center, so player.y is at center
        // We need to adjust mob's Y position to represent center point for accurate targeting
        const mobCenterY = this.y - (this.sprite.height * 0.5); // Convert bottom to center
        
        const distance = Phaser.Math.Distance.Between(
            this.x, mobCenterY, 
            player.x, player.y
        );

        if (distance === 0) return { x: 0, y: 0 };

        return {
            x: (player.x - this.x) / distance,
            y: (player.y - mobCenterY) / distance
        };
    }

    getDistanceToPlayer() {
        const player = this.getPlayer();
        if (!player) return Infinity;

        // Use the same center-based calculation as getDirectionToPlayer for consistency
        const mobCenterY = this.y - (this.sprite.height * 0.5);

        return Phaser.Math.Distance.Between(
            this.x, mobCenterY,
            player.x, player.y
        );
    }

    canSeePlayer() {
        const distance = this.getDistanceToPlayer();
        const canSee = distance <= this.detectionRange && !this.isDead;
        return canSee;
    }

    isPlayerInAttackRange() {
        const distance = this.getDistanceToPlayer();
        return distance <= this.attackRange && !this.isDead;
    }

    // Combat methods
    takeDamage(amount, damageDirection = null) {
        if (this.isDead || this.isInvulnerable) return false;

        this.currentHealth -= amount;
        this.lastDamageDirection = damageDirection;

        // Play damage sound
        this.playDamageSound();

        // Make temporarily invulnerable
        this.isInvulnerable = true;
        this.invulnerabilityTimer = this.invulnerabilityDuration;
        this.sprite.setAlpha(0.7); // Visual feedback

        // Check if dead
        if (this.currentHealth <= 0) {
            this.currentHealth = 0;
            this.stateMachine.changeState('DeathState');
        } else {
            // Change to hurt state
            this.stateMachine.changeState('HurtState');
        }

        return true;
    }

    dealDamageToPlayer() {
        const player = this.getPlayer();
        if (player && player.modifyHealth) {
            player.modifyHealth(-this.damage); // Use negative value to reduce health
        }
    }

    executeSkillEffect() {
        // Play skill sound
        this.playSkillSound();
        
        // Override in subclasses for specific skill effects
        // Base implementation for shield bash or area damage
        const player = this.getPlayer();
        if (player && this.getDistanceToPlayer() <= this.attackRange * 1.5) {
            this.dealDamageToPlayer();
        }
    }

    // Lifecycle methods
    onDeathAnimationComplete() {
        // Drop loot, give experience, etc.
        this.onDeath();
        
        // Remove from scene
        this.destroy();
    }

    onDeath() {
        // Play death sound
        this.playDeathSound();
        
        // Override in subclasses for specific death behavior
        // Base implementation: emit death event
        this.scene.events.emit('mobDied', this);
    }

    destroy() {
        if (this.sprite) {
            this.sprite.destroy();
        }
        // Clean up any other resources
    }

    // Utility methods
    getHealthPercentage() {
        return this.currentHealth / this.maxHealth;
    }

    isAlive() {
        return !this.isDead && this.currentHealth > 0;
    }

    // Debug methods
    getDebugInfo() {
        return {
            mobType: this.mobType,
            enemyType: this.enemyType,
            health: `${this.currentHealth}/${this.maxHealth}`,
            state: this.stateMachine.getCurrentStateName(),
            position: { x: Math.round(this.x), y: Math.round(this.y) },
            distanceToPlayer: Math.round(this.getDistanceToPlayer())
        };
    }
} 